import type BusinessRule from '~/types/BusinessRule';

import { type JSONAutomationRule } from '~/types/AutomationRule';
import { type AutomationRuleError } from '~/types/AutomationRuleError';
import { type ParseBusinessRulesReturnType } from '~/utils/businessRules';

import { ActionKeys } from './constants';

export const fetchBusinessRules = () =>
  <const>{
    type: ActionKeys.FETCH_BUSINESS_RULES,
  };

export const fetchBusinessRulesSuccess = (payload: ParseBusinessRulesReturnType) =>
  <const>{
    payload,
    type: ActionKeys.FETCH_BUSINESS_RULES_SUCCESS,
  };

export const fetchBusinessRulesError = (payload: { errorMessage: string }) =>
  <const>{
    payload,
    type: ActionKeys.FETCH_BUSINESS_RULES_ERROR,
  };

export const openDeleteModal = () =>
  <const>{
    type: ActionKeys.AUTOMATIONS_OPEN_DELETE_MODAL,
  };

export const closeDeleteModal = () =>
  <const>{
    type: ActionKeys.AUTOMATIONS_CLOSE_DELETE_MODAL,
  };

export const deleteRule = (ruleId: string) =>
  <const>{
    payload: {
      ruleId,
    },
    type: ActionKeys.AUTOMATIONS_DELETE_RULE,
  };

export const deleteRuleSuccess = () =>
  <const>{
    type: ActionKeys.AUTOMATIONS_DELETE_RULE_SUCCESS,
  };

export const deleteRuleError = (errorMessage: string) =>
  <const>{
    payload: {
      errorMessage,
    },
    type: ActionKeys.AUTOMATIONS_DELETE_RULE_ERROR,
  };

export const openAddRuleModal = () =>
  <const>{
    type: ActionKeys.AUTOMATIONS_OPEN_ADD_RULE_MODAL,
  };

export const closeAddRuleModal = () =>
  <const>{
    type: ActionKeys.AUTOMATIONS_CLOSE_ADD_RULE_MODAL,
  };

export const createRule = (ruleFormData: Maybe<JSONAutomationRule>) =>
  <const>{
    payload: {
      ruleFormData,
    },
    type: ActionKeys.AUTOMATIONS_CREATE_RULE,
  };

export const createRuleSuccess = () =>
  <const>{
    type: ActionKeys.AUTOMATIONS_CREATE_RULE_SUCCESS,
  };

export const setRuleError = (error: AutomationRuleError) =>
  <const>{
    payload: {
      error,
    },
    type: ActionKeys.AUTOMATIONS_SET_RULE_ERROR,
  };

export const reorderRules = (rules: BusinessRule[]) =>
  <const>{
    payload: {
      rules,
    },
    type: ActionKeys.AUTOMATIONS_REORDER_RULES,
  };

export const reorderRulesSuccess = (rules: BusinessRule[]) =>
  <const>{
    payload: {
      rules,
    },
    type: ActionKeys.AUTOMATIONS_REORDER_RULES_SUCCESS,
  };

export const reorderRulesError = (errorMessage: string) =>
  <const>{
    payload: {
      errorMessage,
    },
    type: ActionKeys.AUTOMATIONS_REORDER_RULES_ERROR,
  };

export const reprocessRules = () =>
  <const>{
    type: ActionKeys.AUTOMATIONS_REPROCESS_RULES,
  };

export const getRuleReprocessStatus = () =>
  <const>{
    type: ActionKeys.AUTOMATIONS_GET_REPROCESS_STATUS,
  };

export const startRuleReprocessStatusPolling = () =>
  <const>{
    type: ActionKeys.AUTOMATIONS_REPROCESS_RULES_POLL_START,
  };

export const stopRuleReprocessStatusPolling = () =>
  <const>{
    type: ActionKeys.AUTOMATIONS_REPROCESS_RULES_POLL_END,
  };

export const updateRuleReprocessStatus = (status: string) =>
  <const>{
    payload: {
      status,
    },
    type: ActionKeys.AUTOMATIONS_UPDATE_REPROCESS_STATUS,
  };

export const resetReprocessingPendingNotification = () =>
  <const>{
    type: ActionKeys.AUTOMATIONS_RESET_RULE_REPROCESS_PENDING_NOTIF,
  };

export const resetReprocessingCompleteNotification = () =>
  <const>{
    type: ActionKeys.AUTOMATIONS_RESET_RULE_REPROCESS_COMPLETE_NOTIF,
  };

export const closeReprocessingPendingNotification = () =>
  <const>{
    type: ActionKeys.AUTOMATIONS_CLOSE_RULE_REPROCESS_PENDING_NOTIF,
  };

export const closeReprocessingCompleteNotification = () =>
  <const>{
    type: ActionKeys.AUTOMATIONS_CLOSE_RULE_REPROCESS_COMPLETE_NOTIF,
  };

export type AutomationsActions =
  | ReturnType<typeof closeAddRuleModal>
  | ReturnType<typeof closeDeleteModal>
  | ReturnType<typeof closeReprocessingCompleteNotification>
  | ReturnType<typeof closeReprocessingPendingNotification>
  | ReturnType<typeof createRule>
  | ReturnType<typeof createRuleSuccess>
  | ReturnType<typeof deleteRule>
  | ReturnType<typeof deleteRuleError>
  | ReturnType<typeof deleteRuleSuccess>
  | ReturnType<typeof fetchBusinessRules>
  | ReturnType<typeof fetchBusinessRulesError>
  | ReturnType<typeof fetchBusinessRulesSuccess>
  | ReturnType<typeof getRuleReprocessStatus>
  | ReturnType<typeof openAddRuleModal>
  | ReturnType<typeof openDeleteModal>
  | ReturnType<typeof reorderRules>
  | ReturnType<typeof reorderRulesError>
  | ReturnType<typeof reorderRulesSuccess>
  | ReturnType<typeof reprocessRules>
  | ReturnType<typeof resetReprocessingCompleteNotification>
  | ReturnType<typeof resetReprocessingPendingNotification>
  | ReturnType<typeof setRuleError>
  | ReturnType<typeof startRuleReprocessStatusPolling>
  | ReturnType<typeof stopRuleReprocessStatusPolling>
  | ReturnType<typeof updateRuleReprocessStatus>;
